/**
 * USP Slider Component
 * Handles both desktop tabbed interface and mobile accordion
 * Supports multiple instances on the same page
 */

export function initUspSliders() {
    const sliderContainers = document.querySelectorAll('[data-usp-slider]');
    
    sliderContainers.forEach(container => {
        const tabs = container.querySelectorAll('.usp-tab');
        const contentWrappers = container.querySelectorAll('.usp-content-wrapper');
        
        function activateTab(tab: Element, shouldFocus = true) {
            const targetId = tab.getAttribute('data-tab');
            const isMobile = container.classList.contains('md:hidden');
            
            // Update all tabs within this container
            tabs.forEach(t => {
                const isActive = t === tab;
                t.classList.toggle('active', isActive);
                t.classList.toggle('border-hst-red', isActive);
                t.classList.toggle('bg-hst-red', isActive);
                t.classList.toggle('bg-opacity-5', isActive);
                t.classList.toggle('border-gray-200', !isActive);
                t.setAttribute('aria-selected', isActive ? 'true' : 'false');
                
                // WCAG: All tabs must be reachable via Tab key
                t.setAttribute('tabindex', '0');
                
                // Handle accordion icon for mobile
                const accordionIcon = t.querySelector('.accordion-icon');
                if (accordionIcon instanceof HTMLElement) {
                    accordionIcon.style.transform = (t === tab && isActive) ? 'rotate(180deg)' : 'rotate(0deg)';
                }
            });
            
            // Update content visibility
            contentWrappers.forEach(wrapper => {
                const wrapperContent = wrapper.querySelector('[data-content]');
                const contentId = wrapperContent?.getAttribute('data-content') || wrapper.getAttribute('data-content');
                const isActive = contentId === targetId;
                
                if (wrapper instanceof HTMLElement) {
                    if (isMobile) {
                        // Mobile: max-height accordion
                        wrapper.style.maxHeight = isActive ? '2000px' : '0';
                        wrapper.classList.toggle('active', isActive);
                    } else {
                        // Desktop: opacity + position + pointer events
                        if (isActive) {
                            wrapper.classList.remove('opacity-0', 'absolute');
                            wrapper.classList.add('opacity-100', 'relative');
                            wrapper.style.pointerEvents = 'auto';
                        } else {
                            wrapper.classList.remove('opacity-100', 'relative');
                            wrapper.classList.add('opacity-0', 'absolute');
                            wrapper.style.pointerEvents = 'none';
                        }
                    }
                }
            });
            
            // Set focus only on desktop and when explicitly requested
            if (!isMobile && shouldFocus && tab instanceof HTMLElement) {
                tab.focus();
            }
        }
        
        function handleClick(this: Element) {
            // Don't auto-focus on mobile to prevent page jump
            const isMobile = container.classList.contains('md:hidden');
            activateTab(this, !isMobile);
        }
        
        function handleKeydown(this: Element, event: KeyboardEvent) {
            const isMobile = container.classList.contains('md:hidden');
            const tabsArray = Array.from(tabs);
            const currentIndex = tabsArray.indexOf(this);
            let targetIndex: number;
            
            // Enter/Space activates tab (both mobile and desktop)
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                activateTab(this, !isMobile);
                return;
            }
            
            // Desktop: Arrow keys also navigate (optional enhancement)
            if (!isMobile) {
                switch (event.key) {
                    case 'ArrowLeft':
                    case 'ArrowUp':
                        event.preventDefault();
                        targetIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
                        activateTab(tabs[targetIndex], true);
                        break;
                    case 'ArrowRight':
                    case 'ArrowDown':
                        event.preventDefault();
                        targetIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
                        activateTab(tabs[targetIndex], true);
                        break;
                    case 'Home':
                        event.preventDefault();
                        activateTab(tabs[0], true);
                        break;
                    case 'End':
                        event.preventDefault();
                        activateTab(tabs[tabs.length - 1], true);
                        break;
                }
            }
        }
        
        // Attach event listeners
        tabs.forEach(tab => {
            tab.addEventListener('click', handleClick);
            tab.addEventListener('keydown', handleKeydown as EventListener);
        });
    });
    
    return {
        destroy: () => {
            // Cleanup if needed
        }
    };
}

